import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import useSessionStorageFilters from '@/js/libs/hooks/useSessionStorageFilters';

import { StorageKey, StorageScope } from 'src/hooks/useSessionStorage';
import { TaskTableSearchKey, TaskTableStatus } from '../types';

export const useTableStatus = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { loadStorageFilter, saveStorageFilter } = useSessionStorageFilters<Record<string, string | undefined>, StorageScope, StorageKey>(
    StorageScope.SALES_TASK_TABLE,
    StorageKey.SAVED_SEARCH,
  );
  const status: TaskTableStatus =
    (searchParams.get(TaskTableSearchKey.STATUS) as TaskTableStatus) ?? loadStorageFilter(TaskTableSearchKey.STATUS) ?? TaskTableStatus.ALL;

  const updateStatus = useCallbackRef((value: string | number) => {
    const newStatus = value as TaskTableStatus;
    setSearchParams(
      (params) => {
        params.set(TaskTableSearchKey.STATUS, newStatus);
        return params;
      },
      { replace: true },
    );
    saveStorageFilter(TaskTableSearchKey.STATUS, newStatus);
  });

  React.useEffect(() => {
    const searchValue = searchParams.get(TaskTableSearchKey.STATUS);
    const storageValue = loadStorageFilter(TaskTableSearchKey.STATUS);
    if (!storageValue || (storageValue && searchValue && storageValue !== searchValue)) {
      saveStorageFilter(TaskTableSearchKey.STATUS, searchValue ?? TaskTableStatus.ALL);
    }
    if (!searchValue) {
      setSearchParams(
        (params) => {
          params.set(TaskTableSearchKey.STATUS, storageValue ?? TaskTableStatus.ALL);
          return params;
        },
        { replace: true },
      );
    }
  }, [loadStorageFilter, saveStorageFilter, searchParams, setSearchParams]);

  return React.useMemo(
    () => ({
      status,
      updateStatus,
    }),
    [status, updateStatus],
  );
};
